import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const AddUserModal = ({ onClose, onAddUser }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    role: 'Staff',
    gst_number: '',
    address: '',
    password: '',
    permissions: [],
    status: 'Active',
    logo: null
    
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const availablePermissions = [
    'Full Access',
    'User Management',
    'Create QR Code',
    'Create Product',
    'View Reports',
    'Audit Logs',
    'Export Data',

  ];

  const rolePermissions = {
    'Admin': ['Full Access', 'User Management', 'Create QR Code', 'Create Product', 'View Reports', 'Audit Logs', 'Export Data'],
    'Staff': ['User Management', 'Create QR Code', 'Create Product', 'View Reports'],
    'Client': ['View Reports']
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: rolePermissions[role] || []
    }));
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };
  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      logo: e.target.files[0]
    }));
  };
  const validateForm = () => {
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (validateForm()) {
        try {
            setLoading(true);
            // Create FormData object to send all the data, including file upload
            const formDataWithLogo = new FormData();
            formDataWithLogo.append('company_name', formData.company_name);
            formDataWithLogo.append('email', formData.email);
            formDataWithLogo.append('phone', formData.phone);
            formDataWithLogo.append('role', formData.role);
            formDataWithLogo.append('address', formData.address);
            formDataWithLogo.append('status', formData.status);
            formDataWithLogo.append('permissions', JSON.stringify(formData.permissions)); // Convert permissions to JSON string
            formDataWithLogo.append('password', formData.password);

            // Append logo if available
            if (formData.logo) {
                formDataWithLogo.append('logo', formData.logo);
            }

            const currentUser =JSON.parse(localStorage.getItem('user'));
            if(formData.role === 'Staff' && currentUser){
              formDataWithLogo.append('parent_id',currentUser.id)
            }
            
            const token = localStorage.getItem('token')
            // Send the form data to the backend (API)
            const response = await fetch(import.meta.env.VITE_APP_ADMINURL+'/api/user-data', {
                method: 'POST',
                headers:{
                  Authorization : `Bearer ${token}`
                } ,
                body: formDataWithLogo,  // Send FormData with the logo file
            });

            if (response.ok) {
                const data = await response.json();
                console.log('User added successfully:', data);
                onAddUser(formData);
            } else {
                const error = await response.json();
                console.error('Error adding user:', error);
            }
        } catch (error) {
            console.error('Something went wrong:', error);
        } finally {
            setLoading(false);
        }
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-floating max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Add New User</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-lg nav-transition"
          >
            <Icon name="X" size={20} color="var(--color-text-secondary)" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.company_name ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter Company Name"
                  />
                  {errors.company_name && <p className="mt-1 text-sm text-error">{errors.company_name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.email ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.email && <p className="mt-1 text-sm text-error">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.phone ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter phone number"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-error">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    GST Number
                  </label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => handleInputChange('gst_number', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.gst_number ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter GST number"
                  />
                  {errors.gst_number && <p className="mt-1 text-sm text-error">{errors.gst_number}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Address *
                  </label>
                  <textarea
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.address ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter Address"
                  />
                  {errors.address && <p className="mt-1 text-sm text-error">{errors.address}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Password *
                  </label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent ${
                      errors.department ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Enter Password"
                  />
                  {errors.password && <p className="mt-1 text-sm text-error">{errors.password}</p>}
                </div>
              </div>
            </div>

            {/* Role and Status */}
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">Role & Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Staff">Staff</option>
                    <option value="Client">Client</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">Permissions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availablePermissions.map((permission) => (
                  <label key={permission} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-background nav-transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission)}
                      onChange={() => handlePermissionToggle(permission)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm text-text-primary">{permission}</span>
                  </label>
                ))}
              </div>
              {errors.permissions && <p className="mt-2 text-sm text-error">{errors.permissions}</p>}
            </div>

            {/* Additional Options */}
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">Company Logo</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-background flex items-center justify-end space-x-3">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="text-white"
              iconName="UserPlus"
              loading={loading}
            >
              <span>Add User</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
